import { createFileRoute } from "@tanstack/react-router";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Star,
  Stethoscope,
  Activity,
  HandHeart,
  Sparkles,
  Dumbbell,
  Workflow,
} from "lucide-react";
import logoUrl from "@/assets/rafaelgonzalez_t.png";
import { useState } from "react";
import { AuthDialog } from "@/components/auth-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Rafael González · Centro de Osteopatía Integral · Cangas do Morrazo" },
      {
        name: "description",
        content:
          "Centro de osteopatía integral en Cangas do Morrazo. Osteopatía, kinesioloxía, masaxe deportiva, cadeas musculares, método Mézières e par biomagnético.",
      },
      { property: "og:title", content: "Rafael González · Centro de Osteopatía Integral" },
      {
        property: "og:description",
        content:
          "Tratamento manual de lesións musculoesqueléticas, dor de espalda, columna, contracturas e máis. Cangas do Morrazo.",
      },
    ],
  }),
  component: LandingPage,
});

const SHOW_ACCESS_BUTTON = false;

type LandingPageProps = {
  initialAuthOpen?: boolean;
  authReturnUrl?: string;
  onAuthOpenChange?: (open: boolean) => void;
};

const services = [
  { icon: Stethoscope, title: "Osteopatía" },
  { icon: Activity, title: "Kinesioloxía" },
  { icon: Dumbbell, title: "Masaxe deportiva" },
  { icon: Workflow, title: "Cadeas musculares" },
  { icon: HandHeart, title: "Método Mézières" },
  { icon: Sparkles, title: "Par biomagnético" },
];

const professionals = [
  { name: "Rafael González", role: "Osteópata · Director do centro" },
  { name: "Lucía", role: "Osteópata" },
];

const REVIEWS_URL =
  "https://www.google.com/search?sca_esv=91390b2ecb4203d7&hl=es-US&gl=us&sxsrf=ANbL-n7Vc1VP20RK8miVovCojiYHIKrIHw:1780660395269&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOTLW2TIpOYfGi-OI09_gfWJCN2p6F7IFh6tuE-vTePZtgwG43mSPVILkdpMGt8xQNrrXv94e0zmtlBXpNbeM_6opo7cj4jCrWTkrHKsvKLOYQq-gmsF1A2te313_RqWLjR9-VfOVjozkS46SCotGzwmor5rL&q=Rafael+Gonz%C3%A1lez+Centro+De+Osteopatia+Integral+Opiniones&sa=X&ved=2ahUKEwiol83yhPCUAxUGHIYAHXxIJVEQ0bkNegQIJBAF";
const PHONE = "661545739";
const PHONE_INTL = "34661545739";
const EMAIL = "gonzalez.casas.rafael@gmail.com";
const ADDRESS = "Rúa dos Pinos, 8 · 36947 Cangas do Morrazo (Pontevedra)";
const MAPS_URL = `https://maps.google.com/?q=${encodeURIComponent(
  "Rafael González Centro de Osteopatía Integral, Rúa dos Pinos 8, 36947 Cangas, Pontevedra",
)}`;

export function LandingPage({
  initialAuthOpen = false,
  authReturnUrl,
  onAuthOpenChange,
}: LandingPageProps = {}) {
  const [authOpen, setAuthOpen] = useState(initialAuthOpen);

  const handleAuthOpenChange = (open: boolean) => {
    setAuthOpen(open);
    onAuthOpenChange?.(open);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav */}
      <header className="sticky top-0 z-20 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-background">
              <img
                src={logoUrl}
                alt="Rafael González"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-semibold">Rafael González</span>
              <span className="text-xs text-muted-foreground">Centro de Osteopatía Integral</span>
            </div>
          </div>
          <nav className="hidden items-center gap-6 text-sm md:flex">
            <a href="#servicios" className="text-muted-foreground hover:text-foreground">Servicios</a>
            <a href="#profesionales" className="text-muted-foreground hover:text-foreground">Profesionais</a>
            <a href="#contacto" className="text-muted-foreground hover:text-foreground">Contacto</a>
          </nav>
          {SHOW_ACCESS_BUTTON && (
            <Button type="button" onClick={() => handleAuthOpenChange(true)}>
              Acceder
            </Button>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="border-b">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:py-24">
          <div className="order-2 flex justify-center md:order-1 md:justify-start">
            <div className="flex aspect-square w-full max-w-sm items-center justify-center rounded-2xl border bg-card p-10">
              <img
                src={logoUrl}
                alt="Centro de Osteopatía Rafael González"
                className="h-full w-full object-contain"
              />
            </div>
          </div>
          <div className="order-1 md:order-2">
            <p className="mb-3 inline-block rounded-full border bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
              Cangas do Morrazo · Pontevedra
            </p>
            <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
              Rafael González
            </h1>
            <p className="mt-2 text-xl text-primary md:text-2xl">
              Centro de Osteopatía Integral
            </p>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
              Tratamento manual de lesións musculoesqueléticas: dor de espalda, articulacións,
              tendóns, columna vertebral, contracturas, hernias, problemas dixestivos e
              reeducación muscular.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg">
                <a href={`https://wa.me/${PHONE_INTL}`} target="_blank" rel="noreferrer">
                  Pedir cita por WhatsApp
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href={`tel:+${PHONE_INTL}`}>Chamar {PHONE}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Servicios */}
      <section id="servicios" className="border-b">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="mb-10 max-w-2xl">
            <h2 className="text-3xl font-bold tracking-tight">Servizos</h2>
            <p className="mt-2 text-muted-foreground">
              Un enfoque integral para a saúde do teu sistema musculoesquelético.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <Card key={s.title} className="transition-colors hover:border-primary/40">
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <div className="font-semibold">{s.title}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-10 rounded-xl border bg-muted/40 p-6 text-sm leading-relaxed text-muted-foreground">
            Masaxe deportiva, cadeas musculares, método Mézières, par biomagnético,
            kinesioloxía, reeducación muscular, tratamento manual de lesións
            musculoesqueléticas, dor de espalda, articulacións, tendóns, columna vertebral,
            contracturas, hernias, problemas dixestivos e músculos.
          </div>
        </div>
      </section>

      {/* Profesionales */}
      <section id="profesionales" className="border-b">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="mb-10 max-w-2xl">
            <h2 className="text-3xl font-bold tracking-tight">O equipo</h2>
            <p className="mt-2 text-muted-foreground">Profesionais ao teu coidado.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {professionals.map((p) => (
              <Card key={p.name}>
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground">
                    {p.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-sm text-muted-foreground">{p.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contacto / Info */}
      <section id="contacto" className="border-b">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-16 md:grid-cols-2">
          <Card>
            <CardContent className="space-y-5 p-6">
              <h3 className="text-xl font-semibold">Información de contacto</h3>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-3 text-sm hover:text-primary"
              >
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>{ADDRESS}</span>
              </a>
              <a
                href={`tel:+${PHONE_INTL}`}
                className="flex items-center gap-3 text-sm hover:text-primary"
              >
                <Phone className="h-5 w-5 shrink-0 text-primary" />
                <span>{PHONE}</span>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center gap-3 text-sm hover:text-primary"
              >
                <Mail className="h-5 w-5 shrink-0 text-primary" />
                <span>{EMAIL}</span>
              </a>
              <div className="flex items-start gap-3 text-sm">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <div className="font-medium text-foreground">Luns a venres</div>
                  <div className="text-muted-foreground">9:00 – 13:00 · 16:00 – 20:00</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex h-full flex-col justify-between gap-6 p-6">
              <div>
                <div className="mb-3 flex items-center gap-2 text-primary">
                  <Star className="h-5 w-5 fill-current" />
                  <Star className="h-5 w-5 fill-current" />
                  <Star className="h-5 w-5 fill-current" />
                  <Star className="h-5 w-5 fill-current" />
                  <Star className="h-5 w-5 fill-current" />
                </div>
                <h3 className="text-xl font-semibold">Opinións dos pacientes</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Le as reseñas verificadas en Google sobre o noso traballo no centro.
                </p>
              </div>
              <Button asChild variant="outline">
                <a href={REVIEWS_URL} target="_blank" rel="noreferrer">
                  Ver reseñas en Google
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <AuthDialog
        open={authOpen}
        onOpenChange={handleAuthOpenChange}
        returnUrl={authReturnUrl}
      />

      <footer className="bg-background">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 py-8 text-xs text-muted-foreground md:flex-row">
          <div>© {new Date().getFullYear()} Rafael González · Centro de Osteopatía Integral</div>
          <div>{ADDRESS}</div>
        </div>
      </footer>
    </div>
  );
}



